'use client';

import React, { useEffect, useRef, useState } from 'react';
import '../../styles/sidenav/overlays.css';
import { usePortal } from './portal.js';

// Global class names (see overlays.css): identical in the ESM, CJS and UMD builds.
const styles = {
  tooltipTrigger: 'sidenav-tooltip-trigger',
  tooltip: 'sidenav-tooltip',
  tooltipContent: 'sidenav-tooltip__content',
  tooltipArrow: 'sidenav-tooltip__arrow',
  right: 'sidenav-tooltip--right',
  left: 'sidenav-tooltip--left',
  top: 'sidenav-tooltip--top',
  bottom: 'sidenav-tooltip--bottom',
  light: 'sidenav-tooltip--light',
  'arrow-right': 'sidenav-tooltip__arrow--right',
  'arrow-left': 'sidenav-tooltip__arrow--left',
  'arrow-top': 'sidenav-tooltip__arrow--top',
  'arrow-bottom': 'sidenav-tooltip__arrow--bottom',
};

/**
 * Componente Tooltip para mostrar información contextual en estado colapsado
 * Incluye posicionamiento inteligente y animaciones suaves
 */
const Tooltip = ({ 
  children, 
  content, 
  placement = 'right', 
  delay = 200,
  offset = 8,
  disabled = false,
  className = '',
  visible = false,
  target = null,
  position = null,
  ...props 
}) => {
  const inPortal = usePortal();
  const [isVisible, setIsVisible] = useState(visible);
  const [calcPosition, setCalcPosition] = useState(position || { top: 0, left: 0 });
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);

  // Usar posición controlada externamente si se proporciona
  React.useEffect(() => {
    if (position) {
      setCalcPosition(position);
    }
  }, [position]);

  React.useEffect(() => {
    setIsVisible(visible);
  }, [visible]);

  // Función para calcular la posición del tooltip
  const calculatePosition = () => {
    // Si se proporciona position externamente, no calcular
    if (position) return;

    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (placement) {
      case 'right':
        top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
        left = triggerRect.right + offset;
        
        // Verificar si se sale del viewport por la derecha
        if (left + tooltipRect.width > viewportWidth) {
          left = triggerRect.left - tooltipRect.width - offset;
        }
        
        // Verificar si se sale del viewport por arriba o abajo
        if (top < 0) {
          top = offset;
        } else if (top + tooltipRect.height > viewportHeight) {
          top = viewportHeight - tooltipRect.height - offset;
        }
        break;

      case 'left':
        top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
        left = triggerRect.left - tooltipRect.width - offset;
        
        // Verificar si se sale del viewport por la izquierda
        if (left < 0) {
          left = triggerRect.right + offset;
        }
        
        // Verificar si se sale del viewport por arriba o abajo
        if (top < 0) {
          top = offset;
        } else if (top + tooltipRect.height > viewportHeight) {
          top = viewportHeight - tooltipRect.height - offset;
        }
        break;

      case 'top':
        top = triggerRect.top - tooltipRect.height - offset;
        left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        
        // Verificar si se sale del viewport por arriba
        if (top < 0) {
          top = triggerRect.bottom + offset;
        }
        
        // Verificar si se sale del viewport por los lados
        if (left < 0) {
          left = offset;
        } else if (left + tooltipRect.width > viewportWidth) {
          left = viewportWidth - tooltipRect.width - offset;
        }
        break;

      case 'bottom':
        top = triggerRect.bottom + offset;
        left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
        
        // Verificar si se sale del viewport por abajo
        if (top + tooltipRect.height > viewportHeight) {
          top = triggerRect.top - tooltipRect.height - offset;
        }
        
        // Verificar si se sale del viewport por los lados
        if (left < 0) {
          left = offset;
        } else if (left + tooltipRect.width > viewportWidth) {
          left = viewportWidth - tooltipRect.width - offset;
        }
        break;

      default:
        break;
    }

    setCalcPosition({ top, left });
  };

  // Manejar mouse enter
  const handleMouseEnter = () => {
    if (disabled) return;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };

  // Manejar mouse leave
  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    setIsVisible(false);
  };

  // Calcular posición cuando el tooltip se hace visible
  useEffect(() => {
    if (isVisible) {
      calculatePosition();
    }
  }, [isVisible, placement, offset]);

  // Recalcular posición en resize
  useEffect(() => {
    const handleResize = () => {
      if (isVisible) {
        calculatePosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isVisible]);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // No renderizar si no hay contenido
  if (!content) {
    return children ? children : null;
  }

  // Si se proporciona target y visible, renderizar solo el tooltip (modo controlado)
  if (target && visible) {
    return inPortal(
      <div
        ref={tooltipRef}
        className={`${styles.tooltip} ${styles[placement]} ${className}`}
        style={{
          position: 'fixed',
          top: `${calcPosition.top}px`,
          left: `${calcPosition.left}px`,
                  }}
      >
        <div className={styles.tooltipContent}>
          {content}
        </div>
        <div className={`${styles.tooltipArrow} ${styles[`arrow-${placement}`]}`} />
      </div>
    );
  }

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={styles.tooltipTrigger}
        {...props}
      >
        {children}
      </div>
      
      {isVisible && inPortal(
        <div
          ref={tooltipRef}
          className={`${styles.tooltip} ${styles[placement]} ${className}`}
          style={{
            position: 'fixed',
            top: `${calcPosition.top}px`,
            left: `${calcPosition.left}px`,
                      }}
        >
          <div className={styles.tooltipContent}>
            {content}
          </div>
          <div className={`${styles.tooltipArrow} ${styles[`arrow-${placement}`]}`} />
        </div>
      )}
    </>
  );
};

export default Tooltip;